import { action, computed, makeObservable, observable } from 'mobx';
import merge from 'lodash.merge';

import { Step, Project } from './';
import { entityPool, Model } from '../../core/engine';
import { ManyToOne, OneToMany } from '../../core/engine/decorators';
import { api } from '../../api';

export class Stage extends Model {
  /** The stage's name **/
  name: string;

  order: number;

  @OneToMany('stage')
  steps: Step[];

  projectId: string;

  startDate?: string;

  dueDate?: string;

  @ManyToOne('stages')
  project: Project | null = null;

  get totalSteps() {
    return this.steps.length;
  }

  get completedSteps() {
    return this.steps.reduce((acc, step) => (step.done ? acc + 1 : acc), 0);
  }

  constructor() {
    super('stages');

    this.steps = [];

    makeObservable<Stage>(this, {
      _id: observable,
      createdAt: observable,
      updatedAt: observable,
      projectId: observable,
      name: observable,
      order: observable,
      steps: observable,
      totalSteps: computed,
      completedSteps: computed,
      insertStep: action,
      removeStep: action,
      applyReorderSteps: action,
    });
  }

  toPOJO(): Record<string, any> {
    return {
      _id: this._id,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      projectId: this.projectId,
      name: this.name,
      order: this.order,
      steps: this.steps.map((step) => step.toPOJO()),
      totalSteps: this.totalSteps,
      completedSteps: this.completedSteps,
    };
  }

  override async update(values: Partial<Omit<this, '_id'>>) {
    /**
     * If stages where reordered, update local state
     */
    if (values.order) {
      const stages = Array.from(this.project.stages);
      stages.sort((a, b) => a.order - b.order);

      const currentIndex = stages.findIndex((s) => s._id === this._id);

      if (currentIndex !== -1) {
        stages.splice(currentIndex, 1);
      }

      stages.splice(values.order - 1, 0, this);

      for (let i = 0; i < stages.length; i++) {
        stages[i].order = i + 1;
      }
    }

    await api.patch(`/projects/${this.projectId}/stages/${this._id}`, values).then(({ data }) => {
      merge(this, values);
      return data;
    });

  }

  override async delete() {
    const projectId = this.projectId;
    await api.delete(`/projects/${projectId}/stages/${this._id}`);
    entityPool.delete(this);

    /**
     * If stages where reordered, update local order state
     */
    const stages = Array.from(this.project.stages);
    stages.sort((a, b) => a.order - b.order);

    for (let i = 0; i < stages.length; i++) {
      stages[i].order = i + 1;
    }
  }

  static async create(projectId: string, name: string): Promise<Stage> {
    const { data } = await api.post(`/projects/${projectId}/stages`, { name });
    const stage = Object.assign(new Stage(), data);
    entityPool.insert(stage);
    return stage;
  }

  static override getOne(id: string): Stage | undefined {
    return Model.getOne(id) as Stage;
  }

  static override getAll(): Stage[] {
    return Model.getAll().filter((m) => m.$name === 'stages') as Stage[];
  }

  insertStep(step: Step, position?: number) {
    const stepIds = this.steps?.map((step) => step._id) || [];
    const order = position === undefined ? this.steps.length + 1 : position;

    if (stepIds.includes(step._id)) {
      return;
    }

    step.order = order;
    const finalSteps = [...this.steps];
    this.steps.splice(position, 0, step);
    this.steps = finalSteps;
  }

  removeStep(stepId: string) {
    this.steps = this.steps.filter((step) => step._id !== stepId);

    this.steps = this.steps.map((step, index) => {
      step.order = index + 1;
      return step;
    });
  }

  updateStepOrder(stepId: string, order: number) {
    const step = this.steps.find((step) => step._id === stepId);
    if (!step) return;

    this.steps = this.steps.filter((step) => step._id !== stepId);
    this.steps.splice(order - 1, 0, step);

    this.steps = this.steps.map((step, index) => {
      step.order = index + 1;
      return step;
    });

    step.update({
      stageId: this._id,
      order,
    });
  }

  applyReorderSteps() {
    this.steps = this.steps.sort((a, b) => a.order - b.order);
  }

  reorderShootingDays() {
    const shootingDaysWithoutStartDate: Step[] = [];
    const shootingDaysWithStartDate: Step[] = [];

    this.steps.forEach((step) => {
      if (!step.startDate) {
        shootingDaysWithoutStartDate.push(step);
      } else {
        shootingDaysWithStartDate.push(step);
      }
    });

    this.steps = [
      ...shootingDaysWithStartDate.sort(
        (a, b) => new Date(a!.startDate!).getTime() - new Date(b!.startDate!).getTime(),
      ),
      ...shootingDaysWithoutStartDate.sort(
        (a, b) => new Date(a!.createdAt).getTime() - new Date(b!.createdAt).getTime(),
      ),
    ];

    for (let i = 0; i < this.steps.length; i++) {
      this.steps[i].order = i + 1;
    }
  }
}
